import { MentionItem } from '@flatfile/agent-ui'
import { cn, Icon, RichMentionsContext } from '@flatfile/baseline'
import { Portal } from '@radix-ui/react-portal'
import { useContext } from 'react'

export type AgentMentionItem<T = object> = MentionItem & T

// Extended version of the item that will be inserted in the text
export type FormattedMentionItem<T = object> = AgentMentionItem<T> & {
  // Format the display text to show the name - this is now redundant as displayText is required in BaseMentionItem
}

export const getConfigs = (data: Record<string, string>[]) => {
  return [
    {
      query: /@([a-zA-Z0-9_-]+)?/,
      match: /(@\w+)\|([^>]+)/g,
      matchDisplay: '$1',
      customizeFragment: (fragment: HTMLSpanElement, final: boolean) => {
        fragment.className = `bg-gray-${
          final ? '100' : '50'
        } rounded-sm px-1 py-[1px]`
      },
      onMention: (text: string) => {
        const search = text.substr(1) // remove '@'
        return data.filter(
          (item: Record<string, string>) =>
            !search || item.name.toLowerCase().includes(search.toLowerCase())
        )
      },
    },
  ]
}

const getAdjustedMentionPosition = (opened: {
  x: number
  y: number
  element: HTMLElement
}) => {
  if (!opened) return { left: 0, top: 0 }

  const container = opened.element.offsetParent

  if (container) {
    const containerRect = container.getBoundingClientRect()

    return {
      left: opened.x - containerRect.left + containerRect.left + window.scrollX,
      top: opened.y - containerRect.top + containerRect.top + window.scrollY,
    }
  }

  return { left: opened.x + window.scrollX, top: opened.y + window.scrollY }
}

export function MentionsPopover() {
  const { opened, index, results, setActiveItemIndex, selectItem } =
    useContext(RichMentionsContext)

  const { left, top } = getAdjustedMentionPosition(opened)

  const onHoverItem = (index: number) => () => setActiveItemIndex(index)
  if (!(opened && results.length)) return null
  return (
    <Portal>
      <div
        className='fixed bg-base-white p-0 border border-gray-200 shadow-sm text-sm flex flex-col w-full max-w-[240px] max-h-[300px] overflow-y-auto rounded-md translate-x-[20px] translate-y-[8px] -mt-2 z-[9999]'
        style={{
          left: `${left}px`,
          top: `${top}px`,
          maxWidth: '200px',
          marginTop: '0px',
        }}
      >
        {results.map((item, i) => {
          // Use the displayText property for showing the human-readable text
          const displayText = item.displayText || ''

          return (
            <button
              type='button'
              className={cn(
                'w-full p-2 cursor-pointer hover:bg-gray-100 rounded-md flex items-center justify-between',
                i === index && 'bg-gray-100'
              )}
              key={item.ref}
              onClick={() => selectItem(item)}
              onMouseOver={onHoverItem(i)}
            >
              <span className='flex items-center min-w-0 flex-shrink overflow-hidden mr-1.5'>
                <span className='truncate max-w-[120px]'>{displayText}</span>
                {item.type && (
                  <span className='text-gray-500 text-xs pl-1.5 text-[11px] whitespace-nowrap flex-shrink-0'>
                    {item.type}
                  </span>
                )}
              </span>
              {item.groupName && (
                <span className='text-gray-400 text-[11px] flex items-center mr-1 min-w-0 flex-shrink overflow-hidden'>
                  <Icon
                    name='table'
                    className='text-gray-400 mr-1 flex-shrink-0'
                    size={12}
                  />
                  <span className='truncate max-w-[80px]'>
                    {item.groupName}
                  </span>
                </span>
              )}
            </button>
          )
        })}
      </div>
    </Portal>
  )
}
